import {useState} from 'react';
import {useEffect} from 'react';
import {useQuery as useBackendQuery} from 'react-query';
import {getSearchSuggestions} from '../../backendApi';
import {useDebounce} from '../../hooks';
import VectorIcon from '../VectorIcon';
import classNames from 'classnames';

interface SearchSuggestionListProps {
  searchTerm: string;
  onClick: (token: string) => void;
  hide: boolean;
  includeOutOfStock?: boolean;
}

export default function SearchSuggestionList({
  searchTerm,
  onClick,
  hide,
  includeOutOfStock,
}: SearchSuggestionListProps) {
  const [debouncedSearchTerm, setDebouncedSearchTerm, {signal, debouncing}] =
    useDebounce(searchTerm, 500);
  const [suggestions, setSuggestions] = useState<
    ReturnType<typeof getSearchSuggestions> extends Promise<infer U> ? U : never
  >([]);

  const {data: suggestionData, isLoading} = useBackendQuery(
    ['getSearchSuggestions', signal, includeOutOfStock],
    () => getSearchSuggestions(debouncedSearchTerm, includeOutOfStock),
    {retry: false, enabled: Boolean(debouncedSearchTerm) && !debouncing},
  );

  useEffect(() => {
    if (!searchTerm && !debouncing) {
      setSuggestions([]);
    } else if (typeof suggestionData !== 'undefined') {
      setSuggestions(suggestionData);
    }
  }, [debouncing, searchTerm, suggestionData]);

  useEffect(
    () => setDebouncedSearchTerm(searchTerm),
    [setDebouncedSearchTerm, searchTerm],
  );

  return (
    <div
      className={classNames('livesearch__suggested', {
        suggestions: !hide && searchTerm,
      })}
    >
      <div className="livesearch__suggested-title">Suggested</div>
      <div
        className={classNames('livesearch__suggested-noresults', {
          'no-results-suggestions':
            searchTerm &&
            !debouncing &&
            !isLoading &&
            (suggestions?.length ?? 0) === 0,
        })}
      >
        <p>No results matched your search criteria!</p>
        <VectorIcon iconName="empty-bottle-red" />
      </div>
      <ul className="livesearch__suggested-results" id="livesearch-results">
        {suggestions.map((suggestion) => (
          <li key={suggestion.key}>
            {suggestion.handle ? (
              <a href={`/shop/${suggestion.handle}`}>
                <span>{suggestion.value}</span>
                <span className="icon-arrow-right" />
              </a>
            ) : (
              <button onClick={() => onClick(suggestion.value)}>
                {suggestion.value}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
